import { formatUsd } from '~/features/cost-estimate/utils/format-usd';

import type {
  FunnelIssueSeverity,
  FunnelOutcome,
  FunnelOutcomeBucket,
  FunnelProductsQuery,
  FunnelReasonKind,
  FunnelStage,
  FunnelUnavailable,
  FunnelVerdictState,
} from '../types/batch-funnel.api.types';

/** Semantic tone shared by every funnel chip / bar / card. */
export type FunnelTone = 'info' | 'success' | 'warning' | 'error' | 'neutral';

/** Pill classes per tone — design tokens only (no raw palette colours). */
export const FUNNEL_TONE_CHIP: Record<FunnelTone, string> = {
  info: 'bg-info-50 text-info',
  success: 'bg-success-50 text-success',
  warning: 'bg-warning-50 text-warning',
  error: 'bg-error-50 text-error',
  neutral: 'bg-muted text-muted-foreground',
};

/** Solid fills per tone — the outcome stacked bar and legend dots. */
export const FUNNEL_TONE_FILL: Record<FunnelTone, string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  neutral: 'bg-muted-foreground/40',
};

/** Verdict chip tone (§4.4). ATTENTION shares `error` but renders outlined. */
export function verdictTone(state: FunnelVerdictState | null | undefined): FunnelTone {
  switch (state) {
    case 'RUNNING':
      return 'info';
    case 'COMPLETED':
      return 'success';
    case 'WAITING':
    case 'PARTIAL':
      return 'warning';
    case 'ATTENTION':
    case 'FAILED':
      return 'error';
    default:
      return 'neutral';
  }
}

/** Reason-chip tone by kind (§4.6) — EXCLUDED is by design, so it stays neutral. */
export function reasonKindTone(kind: FunnelReasonKind | null | undefined): FunnelTone {
  switch (kind) {
    case 'FAILED':
      return 'error';
    case 'DROPPED':
    case 'NOT_ATTEMPTED':
    case 'UNEXPLAINED':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Outcome-bucket tone (§4.7) — losses warm, live green, still-moving blue. */
export function outcomeBucketTone(bucket: FunnelOutcomeBucket | null | undefined): FunnelTone {
  switch (bucket) {
    case 'LIVE':
    case 'COMPLETED':
      return 'success';
    case 'PUSHED_UNCONFIRMED':
    case 'IN_FLIGHT':
    case 'PENDING':
      return 'info';
    case 'FAILED':
    case 'REJECTED_AT_DESTINATION':
      return 'error';
    case 'DROPPED':
    case 'NOT_ATTEMPTED':
    case 'UNEXPLAINED':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Issue-card tone by severity. */
export function severityTone(severity: FunnelIssueSeverity | null | undefined): FunnelTone {
  if (severity === 'ERROR') return 'error';
  if (severity === 'WARNING') return 'warning';
  return 'info';
}

/** Count or em-dash — `null` means not reached / unknown, never 0 (rule §13.1). */
export function formatFunnelCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString();
}

/** USD via the cost strip's formatter (sub-dollar keeps 4 decimals); em-dash when unknown. */
export function formatFunnelUsd(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : formatUsd(value);
}

/** The per-stage breakdown buckets, in render order, paired with their tone. */
export const STAGE_BREAKDOWN_FIELDS = [
  { field: 'excluded', tone: 'neutral' },
  { field: 'dropped', tone: 'warning' },
  { field: 'failed', tone: 'error' },
  { field: 'notAttempted', tone: 'warning' },
  { field: 'inFlight', tone: 'info' },
  { field: 'unexplained', tone: 'warning' },
] as const satisfies readonly { field: keyof FunnelStage; tone: FunnelTone }[];

/** Indexes `unavailable[]` by field so sections can swap an empty state for
 *  "Not available yet — {reason}" (rule §13.4). */
export function unavailableByField(
  unavailable: FunnelUnavailable[] | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of unavailable ?? []) {
    if (entry.field) map.set(entry.field, entry.reason ?? '');
  }
  return map;
}

/** Buckets whose products are listed as failures at their stage (reason = code). */
const FAILED_LIKE_BUCKETS: ReadonlySet<string> = new Set(['FAILED', 'DROPPED', 'EXCLUDED']);
/** Buckets that list the products that made it through (no reason filter). */
const SUCCEEDED_LIKE_BUCKETS: ReadonlySet<string> = new Set([
  'LIVE',
  'PUSHED_UNCONFIRMED',
  'COMPLETED',
]);
/** Stages the products endpoint can list (§5.1). */
export const FUNNEL_DRILLABLE_STAGES: ReadonlySet<string> = new Set([
  'CURATED',
  'ENRICHED',
  'DELIVERED',
]);

/** Maps an outcome row to the products-endpoint query behind it, or `null`
 *  when that bucket has no per-product list (not attempted, unexplained, …). */
export function outcomeDrillQuery(outcome: FunnelOutcome): FunnelProductsQuery | null {
  const bucket = outcome.bucket ?? '';
  if (!FUNNEL_DRILLABLE_STAGES.has(outcome.stage ?? '')) return null;
  if (bucket === 'REJECTED_AT_DESTINATION') {
    return { stage: 'DELIVERED', outcome: 'REJECTED', reason: outcome.code };
  }
  if (FAILED_LIKE_BUCKETS.has(bucket) && outcome.code) {
    return { stage: outcome.stage, outcome: 'FAILED', reason: outcome.code };
  }
  if (SUCCEEDED_LIKE_BUCKETS.has(bucket)) {
    return { stage: outcome.stage, outcome: 'SUCCEEDED', reason: null };
  }
  return null;
}

/**
 * A delivery-results `productsPath` with its `errorCode` filter removed (other
 * filters such as `outcomes` kept), plus the code that was dropped. `null` when
 * the path has no `errorCode`. Used when the funnel's reason code differs from
 * the per-product code integrations recorded, so the filtered list is empty.
 */
export function withoutErrorCode(
  productsPath: string | null | undefined,
): { path: string; errorCode: string } | null {
  if (!productsPath) return null;
  const queryIndex = productsPath.indexOf('?');
  if (queryIndex === -1) return null;
  const params = new URLSearchParams(productsPath.slice(queryIndex + 1));
  const errorCode = params.get('errorCode');
  if (!errorCode) return null;
  params.delete('errorCode');
  const query = params.toString();
  return {
    path: productsPath.slice(0, queryIndex) + (query ? `?${query}` : ''),
    errorCode,
  };
}
